/**
 * NeoCoreRetirosPage — bandeja del operador para validar la cuenta del
 * asociado y acreditar (o rechazar) un retiro solicitado desde la app.
 *
 * Flujo del operador:
 *   1. Ve retiros 'solicitado' (los más viejos primero) → abre el drawer.
 *   2. Coteja la imagen del comprobante de titularidad contra los datos:
 *      el titular de la cuenta bancaria debe ser el mismo asociado.
 *   3. "Acreditar" → RPC acreditar_retiro_admin (DEBITA el ahorro).
 *      "Rechazar" → RPC rechazar_retiro_admin (pide motivo).
 *
 * La imagen vive en el bucket privado 'retiros'; se muestra con signed URL
 * (TTL 5 min) generada al abrir el drawer.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, DownloadSimple, HandCoins, MagnifyingGlass } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Estado = 'solicitado' | 'acreditado' | 'rechazado'

type RetiroRow = {
  id: string
  asociado_id: string
  cuenta_bancaria_id: string | null
  cuenta_ahorro_id: string | null
  monto: number
  estado: Estado
  comprobante_titularidad_path: string | null
  movimiento_cuenta_id: string | null
  motivo_rechazo: string | null
  forma_pago: string | null
  cuenta_salida_codigo: string | null
  referencia_banco: string | null
  solicitado_at: string
  procesado_at: string | null
  procesado_por_user_id: string | null
  // join asociado
  asociado_nombre: string
  asociado_no: string
  // join cuenta bancaria destino
  banco: string
  tipo_cuenta: string
  numero_cuenta: string
  titular_nombre: string
  cuenta_estado: string | null
  cuenta_comprobante_path: string | null
}

const ESTADO_BADGE: Record<Estado, { label: string; color: string; bg: string }> = {
  solicitado: { label: 'Solicitado', color: 'var(--warning, #d97706)', bg: 'rgba(217,119,6,0.10)' },
  acreditado: { label: 'Acreditado', color: 'var(--ok-ink, #0f8a57)', bg: 'rgba(15,138,87,0.10)' },
  rechazado:  { label: 'Rechazado', color: 'var(--danger, #a32d2d)', bg: 'rgba(163,45,45,0.10)' },
}

const fmtQ = (n: number) =>
  'Q ' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFechaHora = (iso: string | null) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Guatemala',
  })
}

// Fecha "hoy" en hora de Guatemala (YYYY-MM-DD) para filtrar los del día.
const hoyGT = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' })
const esHoyGT = (iso: string | null) =>
  !!iso && new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' }) === hoyGT()

// Códigos de banco ACH (InterBanking / GuateACH, moneda quetzal) según el
// diccionario del banco. Clave = nombre del banco normalizado (solo A-Z0-9).
const BANCO_ACH: Record<string, string> = {
  INDUSTRIAL: '115', BANRURAL: '116', BAC: '142', PROMERICA: '140',
  BAM: '144', AGROMERCANTIL: '144', GYT: '145', GYTCONTINENTAL: '145', GTCONTINENTAL: '145',
  CHN: '104', BANTRAB: '112', BANGUAT: '101', AZTECA: '147',
  BANCOR: '128', REFORMADOR: '128', CITIGUATEMALA: '143', CITINA: '130',
  DECREDITO: '146', FOCCIDENTE: '159', FICOHSA: '139', INMOBILIARIO: '113',
}
const normBanco = (b: string) => (b ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')
const achCodeFor = (banco: string): string | null => BANCO_ACH[normBanco(banco)] ?? null

// Nombre sin tildes/ñ/comas ni caracteres especiales (requisito del archivo ACH).
const stripName = (s: string) =>
  (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z .]/g, ' ').replace(/\s+/g, ' ').trim().toUpperCase()
// Split heurístico: últimos 2 tokens = apellidos, el resto = nombres.
const splitNombre = (full: string): [string, string] => {
  const p = stripName(full).split(' ').filter(Boolean)
  if (p.length <= 1) return [p.join(' '), '']
  return [p.slice(0, -2).join(' '), p.slice(-2).join(' ')]
}

type Filtro = Estado | 'hoy'

export function NeoCoreRetirosPage() {
  const [rows, setRows] = useState<RetiroRow[]>([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState<Filtro>('solicitado')
  const [search, setSearch] = useState('')
  const [drawer, setDrawer] = useState<RetiroRow | null>(null)
  // IDs de retiros elegidos para exportar (solo aplica a 'solicitado').
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('retiro')
      .select(`
        id, asociado_id, cuenta_bancaria_id, cuenta_ahorro_id, monto, estado,
        comprobante_titularidad_path, movimiento_cuenta_id, motivo_rechazo,
        forma_pago, cuenta_salida_codigo, referencia_banco,
        solicitado_at, procesado_at, procesado_por_user_id,
        asociado:asociado_id (no_asociado, nombre_completo),
        cuenta_bancaria:cuenta_bancaria_id (banco, tipo_cuenta, numero_cuenta, titular_nombre, estado, comprobante_titularidad_path)
      `)
      // DESC + límite alto: garantiza que TODOS los solicitados (los más nuevos)
      // entren, así los contadores/KPI/export cuadran (antes se cortaban con limit 300 asc).
      .order('solicitado_at', { ascending: false })
      .limit(2000)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapped: RetiroRow[] = (data ?? []).map((r: any) => ({
      id: r.id,
      asociado_id: r.asociado_id,
      cuenta_bancaria_id: r.cuenta_bancaria_id,
      cuenta_ahorro_id: r.cuenta_ahorro_id,
      monto: Number(r.monto ?? 0),
      estado: r.estado,
      comprobante_titularidad_path: r.comprobante_titularidad_path,
      movimiento_cuenta_id: r.movimiento_cuenta_id,
      motivo_rechazo: r.motivo_rechazo,
      forma_pago: r.forma_pago ?? null,
      cuenta_salida_codigo: r.cuenta_salida_codigo ?? null,
      referencia_banco: r.referencia_banco ?? null,
      solicitado_at: r.solicitado_at,
      procesado_at: r.procesado_at,
      procesado_por_user_id: r.procesado_por_user_id,
      asociado_nombre: r.asociado?.nombre_completo ?? '—',
      asociado_no: r.asociado?.no_asociado ?? '—',
      banco: r.cuenta_bancaria?.banco ?? '—',
      tipo_cuenta: r.cuenta_bancaria?.tipo_cuenta ?? '—',
      numero_cuenta: r.cuenta_bancaria?.numero_cuenta ?? '—',
      titular_nombre: r.cuenta_bancaria?.titular_nombre ?? '—',
      cuenta_estado: r.cuenta_bancaria?.estado ?? null,
      cuenta_comprobante_path: r.cuenta_bancaria?.comprobante_titularidad_path ?? null,
    }))
    setRows(mapped)
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])

  // Auto-refresh cada 60s para que Nataly vea retiros nuevos sin recargar.
  useEffect(() => {
    const t = setInterval(() => { void load() }, 60_000)
    return () => clearInterval(t)
  }, [load])

  const counts = useMemo(() => ({
    hoy: rows.filter((r) => esHoyGT(r.solicitado_at)).length,
    solicitado: rows.filter((r) => r.estado === 'solicitado').length,
    acreditado: rows.filter((r) => r.estado === 'acreditado').length,
    rechazado: rows.filter((r) => r.estado === 'rechazado').length,
  }), [rows])

  // Resumen operativo: cuánto hay pendiente de mover (los 'solicitado').
  const pendientes = useMemo(() => {
    const list = rows.filter((r) => r.estado === 'solicitado')
    return {
      n: list.length,
      total: list.reduce((s, r) => s + r.monto, 0),
      acreditadoHoy: rows
        .filter((r) => r.estado === 'acreditado' && esHoyGT(r.procesado_at))
        .reduce((s, r) => s + r.monto, 0),
    }
  }, [rows])

  // Exporta los retiros SOLICITADOS al formato de carga por archivo ACH de
  // InterBanking (8 columnas, sin encabezado, códigos numéricos, sin tildes).
  const exportarLoteACH = () => {
    // Si hay retiros seleccionados, se exportan SOLO esos. Si no, todos los solicitados.
    const base = rows.filter((r) => r.estado === 'solicitado')
    const pend = selected.size > 0 ? base.filter((r) => selected.has(r.id)) : base
    if (pend.length === 0) {
      alert(selected.size > 0
        ? 'No hay retiros seleccionados para exportar.'
        : 'No hay retiros solicitados para exportar.')
      return
    }
    const sinCodigo: string[] = []
    const lineas: string[] = []
    for (const r of pend) {
      const code = achCodeFor(r.banco)
      if (!code) { sinCodigo.push(`• ${r.asociado_nombre} — ${r.banco} (${r.numero_cuenta})`); continue }
      const prod = (r.tipo_cuenta ?? '').toUpperCase() === 'AHORROS' ? '1' : '0'
      const [nom, ape] = splitNombre(r.asociado_nombre)
      lineas.push([code, prod, '0', nom, ape, r.numero_cuenta, 'Retiro Neocoop', r.monto.toFixed(2)].join(','))
    }
    if (sinCodigo.length > 0) {
      alert(
        'Estos retiros tienen un banco sin código ACH en el diccionario y NO se incluyeron ' +
        '(cargalos manual):\n\n' + sinCodigo.join('\n'),
      )
    }
    if (lineas.length === 0) return

    // Interbanco (carga por archivo) acepta máximo 9 registros por archivo.
    // Si el lote es mayor, se parte en grupos de 9 y se baja un CSV por grupo.
    const MAX = 9
    const fecha = hoyGT().replace(/-/g, '')
    const grupos: string[][] = []
    for (let i = 0; i < lineas.length; i += MAX) grupos.push(lineas.slice(i, i + MAX))

    const bajar = (contenido: string[], nombre: string) => {
      const blob = new Blob([contenido.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nombre
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }

    if (grupos.length === 1) {
      bajar(grupos[0], `retiros_lote_ach_${fecha}.csv`)
    } else {
      // Descargas escalonadas para que el navegador no bloquee múltiples archivos.
      grupos.forEach((g, i) => {
        setTimeout(() => bajar(g, `retiros_lote_ach_${fecha}_parte${i + 1}de${grupos.length}.csv`), i * 400)
      })
      alert(`El lote tiene ${lineas.length} retiros. Interbanco acepta máx. 9 por archivo, ` +
        `así que se bajan ${grupos.length} archivos (grupos de 9). Subilos uno por uno al banco.`)
    }
  }

  const filtered = useMemo(() => {
    let list = filtro === 'hoy'
      ? rows.filter((r) => esHoyGT(r.solicitado_at))
      : rows.filter((r) => r.estado === filtro)
    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter((r) =>
        r.asociado_nombre.toLowerCase().includes(q) ||
        r.asociado_no.toLowerCase().includes(q) ||
        r.numero_cuenta.includes(q) ||
        r.banco.toLowerCase().includes(q),
      )
    }
    // Solicitados/hoy: los más viejos primero (FIFO de procesamiento).
    // Acreditados/rechazados: los más recientes primero.
    const asc = filtro === 'solicitado' || filtro === 'hoy'
    return [...list].sort((a, b) =>
      asc ? (a.solicitado_at < b.solicitado_at ? -1 : 1)
          : (a.solicitado_at < b.solicitado_at ? 1 : -1))
  }, [rows, filtro, search])

  // Selección para exportar solo los retiros elegidos.
  const seleccion = useMemo(() => {
    const list = rows.filter((r) => r.estado === 'solicitado' && selected.has(r.id))
    return { n: list.length, total: list.reduce((s, r) => s + r.monto, 0) }
  }, [rows, selected])
  const solicitadoVisibles = useMemo(
    () => filtered.filter((r) => r.estado === 'solicitado'),
    [filtered],
  )
  const allVisiblesSelected =
    solicitadoVisibles.length > 0 && solicitadoVisibles.every((r) => selected.has(r.id))
  const toggleOne = (id: string) =>
    setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const toggleAllVisibles = () =>
    setSelected((prev) => {
      const n = new Set(prev)
      if (allVisiblesSelected) solicitadoVisibles.forEach((r) => n.delete(r.id))
      else solicitadoVisibles.forEach((r) => n.add(r.id))
      return n
    })
  const limpiarSeleccion = () => setSelected(new Set())

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18, gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>
            Retiros
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Validá la cuenta del asociado y acreditá el retiro.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={exportarLoteACH}
            disabled={counts.solicitado === 0}
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: counts.solicitado === 0 ? 0.5 : 1 }}
            title={seleccion.n > 0
              ? `Exporta solo los ${seleccion.n} retiros seleccionados al .csv de InterBanking (ACH)`
              : 'Genera el .csv de todos los solicitados para Carga por Archivo en InterBanking (ACH). Seleccioná filas para exportar solo esas.'}
          >
            <DownloadSimple size={14} weight="bold" /> Exportar lote ACH ({seleccion.n > 0 ? seleccion.n : counts.solicitado})
          </button>
          <button onClick={() => void load()} className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowsClockwise size={13} weight="bold" /> Refrescar
          </button>
        </div>
      </div>

      {/* Resumen operativo · qué hay pendiente de mover */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{
          flex: 1, minWidth: 200,
          background: pendientes.n > 0 ? 'rgba(217,119,6,0.08)' : 'var(--surface)',
          border: '1px solid', borderColor: pendientes.n > 0 ? 'rgba(217,119,6,0.30)' : 'var(--hairline)',
          borderRadius: 12, padding: '14px 16px',
        }}>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--warning, #d97706)' }}>
            Pendiente de acreditar
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 22, fontWeight: 800, color: 'var(--ink)', fontFeatureSettings: '"tnum"' }}>
            {fmtQ(pendientes.total)}
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted-foreground)', marginLeft: 8 }}>
              {pendientes.n} {pendientes.n === 1 ? 'retiro' : 'retiros'}
            </span>
          </p>
        </div>
        <div style={{
          flex: 1, minWidth: 200,
          background: 'var(--surface)', border: '1px solid var(--hairline)',
          borderRadius: 12, padding: '14px 16px',
        }}>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--ok-ink, #0f8a57)' }}>
            Acreditado hoy
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 22, fontWeight: 800, color: 'var(--ink)', fontFeatureSettings: '"tnum"' }}>
            {fmtQ(pendientes.acreditadoHoy)}
          </p>
        </div>
      </div>

      {/* Filtros + búsqueda */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, padding: '12px 14px', marginBottom: 12,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {([
            ['hoy', 'Hoy', counts.hoy],
            ['solicitado', 'Solicitados', counts.solicitado],
            ['acreditado', 'Acreditados', counts.acreditado],
            ['rechazado', 'Rechazados', counts.rechazado],
          ] as Array<[Filtro, string, number]>).map(([key, label, n]) => {
            const active = filtro === key
            return (
              <button
                key={key}
                onClick={() => setFiltro(key)}
                style={{
                  all: 'unset', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 99,
                  background: active ? 'var(--brand-50)' : 'transparent',
                  color: active ? 'var(--brand-700)' : 'var(--ink-soft)',
                  fontSize: 12, fontWeight: 700,
                  border: '1px solid', borderColor: active ? 'var(--brand-300, transparent)' : 'transparent',
                }}
              >
                {label}
                <span style={{
                  fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 99,
                  background: active ? 'var(--brand)' : 'var(--n-100)',
                  color: active ? '#fff' : 'var(--muted-foreground)',
                  fontFeatureSettings: '"tnum"',
                }}>{n}</span>
              </button>
            )
          })}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--bg)', borderRadius: 8,
          padding: '6px 10px', border: '1px solid var(--hairline)',
          minWidth: 240,
        }}>
          <MagnifyingGlass size={14} weight="bold" color="var(--muted-foreground)" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Asociado, banco, # cuenta…"
            style={{
              flex: 1, border: 0, background: 'transparent',
              outline: 'none', fontSize: 13, fontFamily: 'inherit',
              color: 'var(--ink)',
            }}
          />
        </div>
      </div>

      {/* Barra de selección · aparece cuando hay retiros elegidos */}
      {seleccion.n > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          background: 'var(--brand-50)', border: '1px solid var(--brand-300, var(--brand-200))',
          borderRadius: 10, padding: '10px 14px', marginBottom: 12,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-700)' }}>
            {seleccion.n} {seleccion.n === 1 ? 'retiro seleccionado' : 'retiros seleccionados'} · {fmtQ(seleccion.total)}
          </span>
          <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
            Se exportarán solo estos en el lote ACH.
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={limpiarSeleccion} className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }}>
            Limpiar selección
          </button>
        </div>
      )}

      {/* Tabla */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            <HandCoins size={28} weight="duotone" /><br />
            Sin retiros con este filtro.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--bg)' }}>
                <th style={{ ...th, width: 36 }}>
                  <input
                    type="checkbox"
                    aria-label="Seleccionar todos los solicitados visibles"
                    checked={allVisiblesSelected}
                    disabled={solicitadoVisibles.length === 0}
                    onChange={toggleAllVisibles}
                    style={{ cursor: solicitadoVisibles.length === 0 ? 'default' : 'pointer' }}
                  />
                </th>
                <th style={th}>Fecha</th>
                <th style={th}>Asociado</th>
                <th style={{ ...th, textAlign: 'right' }}>Monto</th>
                <th style={th}>Cuenta destino</th>
                <th style={th}>Titular</th>
                <th style={th}>Estado</th>
                <th style={th}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const badge = ESTADO_BADGE[r.estado]
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                    <td style={{ ...td, width: 36 }}>
                      {r.estado === 'solicitado' && (
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar retiro de ${r.asociado_nombre}`}
                          checked={selected.has(r.id)}
                          onChange={() => toggleOne(r.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtFechaHora(r.solicitado_at)}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.asociado_nombre}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.asociado_no}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontFeatureSettings: '"tnum"', whiteSpace: 'nowrap' }}>
                      {fmtQ(r.monto)}
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.banco}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', fontFeatureSettings: '"tnum"' }}>
                        {r.tipo_cuenta} · {r.numero_cuenta}
                      </div>
                    </td>
                    <td style={td}>{r.titular_nombre}</td>
                    <td style={td}>
                      <span style={{
                        fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 99,
                        background: badge.bg, color: badge.color,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={td}>
                      <button
                        onClick={() => setDrawer(r)}
                        className="btn btn-ghost"
                        style={{ fontSize: 12, padding: '5px 12px' }}
                      >
                        {r.estado === 'solicitado' ? 'Revisar' : 'Ver'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {drawer && (
        <RetiroDrawer
          retiro={drawer}
          onClose={() => setDrawer(null)}
          onChange={() => { void load(); setDrawer(null) }}
        />
      )}
    </div>
  )
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px',
  fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)',
  textTransform: 'uppercase', letterSpacing: 1.2,
}
const td: React.CSSProperties = { padding: '12px 14px' }

// ============================================================
// Drawer de detalle / acciones
// ============================================================

function RetiroDrawer({
  retiro, onClose, onChange,
}: {
  retiro: RetiroRow
  onClose: () => void
  onChange: () => void
}) {
  const [submitting, setSubmitting] = useState(false)
  const [imgUrl, setImgUrl] = useState<string | null>(null)
  const [nota, setNota] = useState('')
  const [motivoRechazo, setMotivoRechazo] = useState('')
  const [formaPago, setFormaPago] = useState<'EFECTIVO' | 'TRANSFERENCIA'>('TRANSFERENCIA')
  const [cuentasSalida, setCuentasSalida] = useState<Array<{ codigo: string; nombre: string; tipo: string }>>([])
  const [cuentaSalida, setCuentaSalida] = useState<string>('')
  const [refBanco, setRefBanco] = useState('')

  // Cuentas de salida (caja + bancos) para la partida contable / conciliación.
  useEffect(() => {
    let cancelled = false
    supabase.rpc('listar_cuentas_salida_retiro').then(({ data }) => {
      if (cancelled || !data) return
      const list = data as Array<{ codigo: string; nombre: string; tipo: string }>
      setCuentasSalida(list)
      // Default: Interbanco (los retiros salen de ahí); si no está, primer banco.
      const interbanco = list.find((c) => c.nombre.toLowerCase().includes('interbanco'))
      const pre = interbanco ?? list.find((c) => c.tipo === 'BANCO')
      if (pre) setCuentaSalida(pre.codigo)
    })
    return () => { cancelled = true }
  }, [])

  // Imagen de titularidad de la cuenta. Desde el flujo nuevo la foto vive en
  // el bucket 'cuentas' (se sube al verificar la cuenta, no en el retiro).
  // Los retiros viejos tenían su propia foto en el bucket 'retiros'.
  // Es OPCIONAL: si falta o falla, no mostramos error (la cuenta ya se
  // verificó antes del retiro en Verificaciones bancarias).
  const comprobantePath = retiro.comprobante_titularidad_path ?? retiro.cuenta_comprobante_path
  const comprobanteBucket = retiro.comprobante_titularidad_path ? 'retiros' : 'cuentas'

  useEffect(() => {
    if (!comprobantePath) { setImgUrl(null); return }
    let cancelled = false
    supabase.storage
      .from(comprobanteBucket)
      .createSignedUrl(comprobantePath, 300) // 5 min TTL
      .then(({ data, error }) => {
        if (cancelled) return
        setImgUrl(error || !data ? null : data.signedUrl)
      })
      .catch(() => { if (!cancelled) setImgUrl(null) })
    return () => { cancelled = true }
  }, [comprobantePath, comprobanteBucket])

  const acreditar = async () => {
    if (formaPago === 'TRANSFERENCIA' && !cuentaSalida) {
      alert('Elegí de qué cuenta de banco sale la transferencia'); return
    }
    if (formaPago === 'TRANSFERENCIA' && !refBanco.trim()) {
      alert('Cargá la referencia de la transferencia antes de acreditar'); return
    }
    setSubmitting(true)
    const { data, error } = await supabase.rpc('acreditar_retiro_admin', {
      p_retiro_id: retiro.id,
      p_forma_pago: formaPago,
      p_cuenta_salida_codigo: formaPago === 'TRANSFERENCIA' ? cuentaSalida : null,
      p_nota: nota || null,
      p_referencia_banco: formaPago === 'TRANSFERENCIA' ? (refBanco.trim() || null) : null,
    })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res = data as any
    if (res && res.ok === false) { alert('No se pudo acreditar: ' + (res.error ?? 'error desconocido')); return }
    if (res && typeof res.saldo_nuevo !== 'undefined') {
      alert('Retiro acreditado. Nuevo saldo de ahorro: ' + fmtQ(Number(res.saldo_nuevo)))
    }
    onChange()
  }

  const rechazar = async () => {
    if (!motivoRechazo.trim()) { alert('Ingresá un motivo'); return }
    setSubmitting(true)
    const { error } = await supabase.rpc('rechazar_retiro_admin', {
      p_retiro_id: retiro.id,
      p_motivo: motivoRechazo,
    })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    onChange()
  }

  const badge = ESTADO_BADGE[retiro.estado]
  const esSolicitado = retiro.estado === 'solicitado'

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,18,25,0.40)', zIndex: 999 }} />
      <aside style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(520px, 100%)', background: '#fff', zIndex: 1000,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-12px 0 32px rgba(0,0,0,0.10)',
      }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Retiro</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
              {retiro.asociado_nombre} · {retiro.asociado_no}
            </p>
          </div>
          <span style={{
            fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 99,
            background: badge.bg, color: badge.color,
            textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
          }}>
            {badge.label}
          </span>
        </div>

        <div style={{ padding: '18px 24px', overflowY: 'auto', flex: 1 }}>
          <div style={{
            background: 'var(--brand-50)', border: '1px solid var(--brand-300, var(--brand-200))',
            borderRadius: 10, padding: '14px 16px', marginBottom: 16,
          }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: 'var(--brand-700)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
              Monto a retirar
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 28, fontWeight: 800, color: 'var(--brand)', fontFeatureSettings: '"tnum"' }}>
              {fmtQ(retiro.monto)}
            </p>
          </div>

          <Info label="Solicitado" value={fmtFechaHora(retiro.solicitado_at)} />
          <Info label="Banco" value={`${retiro.banco} · ${retiro.tipo_cuenta}`} />
          <Info label="Número de cuenta" value={retiro.numero_cuenta} mono />
          <Info label="Titular de la cuenta" value={retiro.titular_nombre} highlight />
          <Info label="Asociado" value={retiro.asociado_nombre} />
          {retiro.cuenta_estado && <Info label="Estado de la cuenta" value={retiro.cuenta_estado} />}
          {retiro.estado !== 'solicitado' && (
            <Info label="Procesado" value={fmtFechaHora(retiro.procesado_at)} />
          )}
          {retiro.estado === 'acreditado' && retiro.forma_pago && (
            <Info label="Forma de pago" value={retiro.forma_pago === 'EFECTIVO' ? 'Efectivo (Caja)' : 'Transferencia'} />
          )}
          {retiro.estado === 'acreditado' && retiro.cuenta_salida_codigo && (
            <Info
              label="Salió de"
              value={cuentasSalida.find((c) => c.codigo === retiro.cuenta_salida_codigo)?.nombre ?? retiro.cuenta_salida_codigo}
            />
          )}
          {retiro.estado === 'acreditado' && retiro.referencia_banco && (
            <Info label="Referencia" value={retiro.referencia_banco} />
          )}
          {retiro.estado === 'acreditado' && (
            <div style={{
              marginTop: 6, marginBottom: 4,
              border: '1px solid rgba(15,138,87,0.22)', background: 'rgba(15,138,87,0.06)',
              borderRadius: 8, padding: '8px 12px', fontSize: 11.5, color: 'var(--ok-ink, #0f8a57)',
            }}>
              ✓ Contabilizado (DEBE ahorro / HABER {retiro.forma_pago === 'EFECTIVO' ? 'caja' : 'banco'}).
              {retiro.forma_pago === 'EFECTIVO' ? ' Registrado en el arqueo de caja.' : ' Conciliá contra el banco de salida.'}
            </div>
          )}
          {retiro.estado === 'rechazado' && retiro.motivo_rechazo && (
            <Info label="Motivo del rechazo" value={retiro.motivo_rechazo} />
          )}

          {/* Verificación de la cuenta · con el flujo nuevo la titularidad se
              revisa ANTES del retiro (en Verificaciones bancarias). Acá solo
              confirmamos el estado; la imagen es opcional (sin error si falta). */}
          <div style={{ marginTop: 20 }}>
            <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
              Verificación de la cuenta
            </p>
            {retiro.cuenta_estado === 'verificada' ? (
              <div style={{
                border: '1px solid rgba(15,138,87,0.25)', background: 'rgba(15,138,87,0.08)',
                borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: 'var(--ok-ink, #0f8a57)',
              }}>
                ✓ Cuenta verificada. La titularidad ya fue revisada.{' '}
                <a href="/neocore/verificaciones-bancarias" style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>
                  Ver verificaciones
                </a>
              </div>
            ) : (
              <div style={{
                border: '1px solid rgba(217,119,6,0.30)', background: 'rgba(217,119,6,0.08)',
                borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: 'var(--warning, #d97706)',
              }}>
                ⚠ La cuenta no está verificada{retiro.cuenta_estado ? ` (${retiro.cuenta_estado})` : ''}.
                Revisala en Verificaciones bancarias antes de acreditar.
              </div>
            )}
          </div>

          {/* Acciones — solo para 'solicitado' */}
          {esSolicitado && (
            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: 0, lineHeight: 1.5 }}>
                Validá que la cuenta sea del asociado antes de acreditar. Esto debita su ahorro.
              </p>

              {/* Forma de pago */}
              <div style={{ display: 'flex', gap: 8 }}>
                {(['TRANSFERENCIA', 'EFECTIVO'] as const).map((f) => {
                  const active = formaPago === f
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFormaPago(f)}
                      style={{
                        all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center',
                        padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                        border: '1px solid', borderColor: active ? 'var(--brand-500, var(--brand-700))' : 'var(--hairline)',
                        background: active ? 'var(--brand-500, var(--brand-700))' : '#fff',
                        color: active ? '#fff' : 'var(--ink-soft)',
                      }}
                    >
                      {f === 'TRANSFERENCIA' ? 'Transferencia' : 'Efectivo'}
                    </button>
                  )
                })}
              </div>

              {formaPago === 'TRANSFERENCIA' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Sale de
                    <select
                      value={cuentaSalida}
                      onChange={(e) => setCuentaSalida(e.target.value)}
                      style={{ padding: '9px 11px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }}
                    >
                      {cuentasSalida.filter((c) => c.tipo === 'BANCO').map((c) => (
                        <option key={c.codigo} value={c.codigo}>{c.nombre}</option>
                      ))}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Referencia <span style={{ color: 'var(--danger, #a32d2d)' }}>*</span>
                    <input
                      type="text"
                      value={refBanco}
                      onChange={(e) => setRefBanco(e.target.value)}
                      placeholder="tu estado de cuenta"
                      style={{ padding: '9px 11px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-mono, monospace)' }}
                    />
                  </label>
                </div>
              )}
              {formaPago === 'EFECTIVO' && (
                <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>
                  Sale de <strong>Caja Principal</strong> · queda registrado como egreso en el arqueo de caja.
                </p>
              )}

              <input
                type="text"
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="Nota (opcional)"
                style={{
                  padding: '9px 11px', border: '1px solid var(--hairline)',
                  borderRadius: 8, fontSize: 13, fontFamily: 'inherit',
                }}
              />
              <button className="btn btn-primary" onClick={acreditar} disabled={submitting}>
                {submitting ? 'Acreditando…' : `Acreditar ${fmtQ(retiro.monto)}`}
              </button>

              <div style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid var(--hairline)' }}>
                <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: 'var(--muted-foreground)', textTransform: 'uppercase' }}>
                  O rechazar (motivo)
                </label>
                <input
                  type="text"
                  value={motivoRechazo}
                  onChange={(e) => setMotivoRechazo(e.target.value)}
                  placeholder="Ej. Titular no coincide con el asociado"
                  style={{
                    marginTop: 6,
                    padding: '10px 12px', border: '1px solid var(--hairline)',
                    borderRadius: 8, fontSize: 13, fontFamily: 'inherit', width: '100%',
                  }}
                />
                <button
                  onClick={rechazar}
                  disabled={submitting || !motivoRechazo.trim()}
                  style={{
                    marginTop: 8, width: '100%',
                    padding: '10px 14px', borderRadius: 8,
                    background: 'transparent',
                    border: '1px solid var(--danger, #a32d2d)',
                    color: 'var(--danger, #a32d2d)',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    opacity: submitting || !motivoRechazo.trim() ? 0.5 : 1,
                  }}
                >
                  Rechazar retiro
                </button>
              </div>
            </div>
          )}

          {/* Comprobante · anexo de consulta (al fondo) */}
          {imgUrl && (
            <div style={{ marginTop: 24 }}>
              <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
                Comprobante · consulta
              </p>
              <a href={imgUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                <img
                  src={imgUrl}
                  alt="Imagen de la cuenta"
                  style={{
                    width: '100%', borderRadius: 10, border: '1px solid var(--hairline)',
                    maxHeight: 340, objectFit: 'contain', background: 'var(--bg)',
                  }}
                />
              </a>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--hairline)' }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ width: '100%' }}>
            Cerrar
          </button>
        </div>
      </aside>
    </>
  )
}

function Info({ label, value, mono, highlight }: { label: string; value: string; mono?: boolean; highlight?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', gap: 16 }}>
      <span style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700 }}>
        {label}
      </span>
      <span style={{
        fontSize: 13, fontWeight: 600,
        color: highlight ? 'var(--brand-700)' : 'var(--ink)',
        fontFamily: mono ? 'IBM Plex Mono, monospace' : undefined,
        fontFeatureSettings: mono ? '"tnum"' : undefined,
        textAlign: 'right',
      }}>
        {value}
      </span>
    </div>
  )
}
